import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { onAuthChange, getUserProfile, signOut } from "../services/authService";
import { syncPendingAuditEvents } from "../services/auditService";
import { cacheUserOrg } from "../services/firestoreSync";
import {
  ROLES,
  roleHasPermission,
  roleCanAccessRoute,
  getNavForRole,
  getDashboardRoute,
  isClinicalRole,
  isSupervisoryRole,
} from "../config/roleConfig";

const AuthContext = createContext(null);

// HIPAA: 15-minute idle timeout
const SESSION_TIMEOUT_MS = 15 * 60 * 1000;
const WARNING_BEFORE_MS = 3 * 60 * 1000; // Warn 3 min before

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const timeoutRef = useRef(null);
  const warningRef = useRef(null);

  // Reset idle timer on any user activity
  const resetIdleTimer = useCallback(() => {
    if (!user) return;
    setShowTimeoutWarning(false);

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);

    // If actively recording, suppress timeout — recording IS activity
    if (isRecording) {
      return;
    }

    // Show warning 3 minutes before logout
    warningRef.current = setTimeout(() => {
      setShowTimeoutWarning(true);
    }, SESSION_TIMEOUT_MS - WARNING_BEFORE_MS);

    // Auto-logout after 15 minutes
    timeoutRef.current = setTimeout(() => {
      console.log("[HIPAA] Session timeout — logging out");
      signOut();
    }, SESSION_TIMEOUT_MS);
  }, [user, isRecording]);

  // When recording state changes, reset or suppress timer accordingly
  useEffect(() => {
    if (!user) return;
    if (isRecording) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
      setShowTimeoutWarning(false);
      console.log("[HIPAA] Timeout suppressed — active recording in progress");
    } else {
      resetIdleTimer();
    }
  }, [isRecording, user]);

  // Expose setRecording for session pages
  const setRecording = useCallback((active) => {
    setIsRecording(active);
  }, []);

  // Listen for user activity
  useEffect(() => {
    if (!user) return;

    const events = ["mousedown", "keydown", "touchstart", "scroll"];
    events.forEach(e => window.addEventListener(e, resetIdleTimer));
    resetIdleTimer();

    return () => {
      events.forEach(e => window.removeEventListener(e, resetIdleTimer));
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
    };
  }, [user, resetIdleTimer]);

  useEffect(() => {
    const unsubscribe = onAuthChange(async (firebaseUser) => {
      if (firebaseUser) {
        // Check if this is a different user than last login — clear clinical data if so
        const lastUid = localStorage.getItem("fieldnotes_last_uid");
        if (lastUid && lastUid !== firebaseUser.uid) {
          console.log("[AUTH] Different user detected — clearing previous user's local data");
          localStorage.removeItem("fieldnotes_local_data");
          localStorage.removeItem("fieldnotes_user_org");
          localStorage.removeItem("fieldnotes_admin_pin");
          localStorage.removeItem("fieldnotes_draft_session");
          localStorage.removeItem("fieldnotes_session_cache");
          const keysToCheck = Object.keys(localStorage);
          keysToCheck.forEach(key => {
            if (key.startsWith("fieldnotes_") && key !== "fieldnotes_last_uid") {
              localStorage.removeItem(key);
            }
          });
        }
        localStorage.setItem("fieldnotes_last_uid", firebaseUser.uid);

        setUser(firebaseUser);
        const userProfile = await getUserProfile(firebaseUser.uid);
        if (userProfile) {
          // Sanitize role — trim whitespace/newlines
          const cleanProfile = {
            ...userProfile,
            role: (userProfile.role || "agent").trim(),
          };
          setProfile(cleanProfile);
          cacheUserOrg(cleanProfile.organization);
          try { localStorage.setItem("fieldnotes_user_profile", JSON.stringify(cleanProfile)); } catch (e) {}
        } else {
          const fallback = {
            displayName: firebaseUser.displayName || firebaseUser.email,
            email: firebaseUser.email,
            role: "agent",
          };
          setProfile(fallback);
          try { localStorage.setItem("fieldnotes_user_profile", JSON.stringify(fallback)); } catch (e) {}
        }

        syncPendingAuditEvents();
      } else {
        setUser(null);
        setProfile(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // ─── Role Helpers ─────────────────────────────────────────────
  const role = (profile?.role || "agent").trim();

  const value = {
    user,
    profile,
    loading,
    isAuthenticated: !!user,

    // Current role
    role,

    // Legacy role checks (kept for backward compatibility)
    isAgent: role === ROLES.AGENT,
    isSupervisor: role === ROLES.SUPERVISOR,
    isAdmin: role === ROLES.ADMIN,

    // New role checks
    isClinician: role === ROLES.CLINICIAN,
    isPrescriber: role === ROLES.PRESCRIBER,
    isFrontDesk: role === ROLES.FRONTDESK,
    isBilling: role === ROLES.BILLING,

    // Grouped checks
    isClinicalRole: isClinicalRole(role),
    isSupervisoryRole: isSupervisoryRole(role),

    // Permission check
    hasPermission: (permission) => roleHasPermission(role, permission),

    // Route access check
    canAccessRoute: (path) => roleCanAccessRoute(role, path),

    // Nav items for current role
    navItems: getNavForRole(role),

    // Where this role should land after login
    homeRoute: getDashboardRoute(role),

    // Session/timeout
    showTimeoutWarning,
    resetIdleTimer,
    isRecording,
    setRecording,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {showTimeoutWarning && user && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999,
          padding: "12px 20px", background: "rgba(236,201,75,0.95)",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#1a202c" }}>
            Session expiring in 3 minutes due to inactivity
          </span>
          <button onClick={resetIdleTimer} style={{
            padding: "6px 16px", borderRadius: 8, fontSize: 12, fontWeight: 700,
            background: "#1a202c", color: "#ecc94b", border: "none", cursor: "pointer",
          }}>Stay Logged In</button>
        </div>
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}